import { BadgeLadderTier } from "@shared/models";

/**
 * Interpolates a 0-100 bar-fill percentage between two already-decided tier boundaries (D1 of the
 * phase-35 plan). Which tier someone is in is server-authoritative and never recomputed here — this
 * only decides where the bar sits BETWEEN two given tier thresholds, which is presentation, not
 * business logic. Used by `ValueLadderCards`'s own fill bar (the Flame card's pip-track rebuild
 * dropped its fill bar and moved to its own `flameSubtitle.ts`, so this is no longer shared with
 * `FlameProgress`).
 *
 * @param total - the person's lifetime total against this ladder (flame or a single core value).
 * @param currentTier - the tier already reached, or `null` if not yet in tier 1.
 * @param nextTier - the tier being filled toward, or `null` if already at the top tier.
 * @returns a number from 0 to 100.
 */
export const ladderFillPercent = (
  total: number,
  currentTier: BadgeLadderTier | null,
  nextTier: BadgeLadderTier | null,
): number => {
  if (!nextTier) return 100; // maxed — current_tier is the top tier, nothing further to fill toward
  const base = currentTier?.threshold ?? 0; // no current tier yet → filling from 0 toward tier 1
  const span = nextTier.threshold - base;
  // span <= 0 is unreachable on real data — badge_definitions thresholds strictly ascend per ladder,
  // and computeLadderTier always picks current_index = next_index - 1 (verified against
  // awardEngine.service.ts:65-81 and badge.service.ts:15-22,35-48) — but the fallback stops a future
  // data bug from becoming `0/0 = NaN` in a `width: NaN%` style, which is a real rendering break, not
  // a defensive guess against nothing.
  return span > 0 ? Math.min(100, Math.max(0, ((total - base) / span) * 100)) : 100;
};

// Per-value copy for a core value's zero-Sparks state, replacing the generic "Not started". There is
// no tier-0 row in `badge_definitions` — the ladder starts at rung 1 (10 Sparks) — so this can't come
// from the api; it has to be a web-side map, and it's keyed on the core value's TITLE, not its id:
// - core_value_id differs between environments, so an id-keyed map would silently mislabel in QA or
//   production.
// - Titles HAVE been renamed at least twice in this product's history, so a title match can miss too
//   — but here that's fine: this is cosmetic copy, and falling back to today's "Not started" is the
//   correct failure mode. (Contrast a separate api migration that also keys on title, for the ladder
//   rung rename: it THROWS on a miss, because there a partial rename would leave real data
//   inconsistent. Same key, different stakes, so this map degrades where that migration can't.)
const ZERO_SPARKS_LABEL_BY_VALUE_TITLE: Record<string, string> = {
  "stay a student": "Blank Page",
  "prioritize helping others": "On Standby",
  "accountability is key": "Clean Slate",
  "raise the bar": "Ground Floor",
  "keep trying. get it done": "On Your Marks",
};

/**
 * The tier-name label shown above a ladder bar. `ValueLadderCards` is the only caller —
 * `FlameProgress` has its own not-started wording (one constant, `FLAME_ZERO_TIER_NAME`, because the
 * flame is a single ladder where these are five).
 *
 * @param currentTier - the tier already reached, or `null` if not yet in tier 1.
 * @param valueTitle - the core value's title, used to pick its zero-Sparks copy when `currentTier`
 *   is `null`. Matched case-insensitively and trimmed; falls back to `"Not started"` for any title
 *   not in `ZERO_SPARKS_LABEL_BY_VALUE_TITLE` (see the comment above it for why this degrades rather
 *   than throws).
 * @returns the current tier's name, or the value's zero-Sparks label when `currentTier` is `null`.
 */
export const ladderTierLabel = (currentTier: BadgeLadderTier | null, valueTitle: string): string => {
  if (currentTier) return currentTier.name;
  const key = valueTitle.trim().toLowerCase();
  return ZERO_SPARKS_LABEL_BY_VALUE_TITLE[key] ?? "Not started";
};

/**
 * The subtitle shown under a ladder bar. Used by `ValueLadderCards`'s own fill bar for its "maxed
 * out" copy (the Flame card's pip-track rebuild dropped its fill bar and moved to its own
 * `flameSubtitle.ts`, so this is no longer shared with `FlameProgress`).
 *
 * @param pointsToNext - points remaining to the next tier, or `null` if already at the top tier.
 * @returns e.g. `"150 pts to next tier"`, or `"Max tier reached"` when `pointsToNext` is `null`.
 */
export const ladderSubtitle = (pointsToNext: number | null): string =>
  pointsToNext === null ? "Max tier reached" : `${pointsToNext} pts to next tier`;
